import type { JSX } from 'react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CreateClassDialog } from '@/features/classes/create-class-dialog';
import { useClasses } from '@/features/classes/hooks';
import { useHasCapability } from '@/lib/capabilities';

const PAGE_SIZE = 20;

/**
 * Classes list (Phase G-6). `classes:read` is held by every organization
 * role (docs/15), so this page renders for every role; only
 * `classes:manage` gates the create action. An optional `?learningSpaceId=`
 * query param narrows the list, matching `GET /classes`'s own filter -
 * used when navigating here from a Learning Space's detail page.
 */
export function ClassesPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const learningSpaceId = searchParams.get('learningSpaceId') ?? undefined;
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading, isError } = useClasses(page, PAGE_SIZE, learningSpaceId);
  const canCreate = useHasCapability('classes:manage');

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Kelas</h1>
          <p className="text-sm text-muted-foreground">Daftar kelas di organisasi ini.</p>
        </div>
        {canCreate && (
          <Button
            type="button"
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            Tambah Kelas
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Gagal memuat daftar kelas.</AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && data !== undefined && data.items.length === 0 && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">Belum ada kelas.</CardContent>
        </Card>
      )}

      {!isLoading && !isError && data !== undefined && data.items.length > 0 && (
        <>
          <ul className="flex flex-col gap-2">
            {data.items.map((classView) => (
              <li key={classView.id}>
                <Link to={`/classes/${classView.id}`}>
                  <Card className="transition-colors hover:bg-secondary/50">
                    <CardContent className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium">{classView.name}</span>
                        {classView.academicYear !== null && (
                          <span className="text-xs text-muted-foreground">
                            {classView.academicYear}
                          </span>
                        )}
                      </div>
                      <Badge variant="secondary" className="capitalize">
                        {classView.status.toLowerCase()}
                      </Badge>
                    </CardContent>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>

          {data.total_pages > 1 && (
            <div className="flex items-center justify-between gap-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => {
                  setPage((current) => Math.max(1, current - 1));
                }}
              >
                Sebelumnya
              </Button>
              <span className="text-sm text-muted-foreground">
                Halaman {data.page} dari {data.total_pages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= data.total_pages}
                onClick={() => {
                  setPage((current) => Math.min(data.total_pages, current + 1));
                }}
              >
                Berikutnya
              </Button>
            </div>
          )}
        </>
      )}

      <CreateClassDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          setPage(1);
        }}
        {...(learningSpaceId !== undefined && { fixedLearningSpaceId: learningSpaceId })}
      />
    </div>
  );
}
